import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import '../styles/FranchiseEnquiry.css';

// Field configuration for maintainability
const formFields = [
  { name: 'name', label: 'Name', type: 'text', placeholder: 'Your Name' },
  { name: 'email', label: 'Email', type: 'email', placeholder: 'Your Email' },
  { name: 'phone', label: 'Phone', type: 'tel', placeholder: 'Your Phone' },
  { name: 'location', label: 'Location', type: 'text', placeholder: 'Your Location' },
  { name: 'investment', label: 'Investment Capacity', type: 'text', placeholder: 'Your Investment Capacity' },
  { name: 'message', label: 'Message', type: 'textarea', placeholder: 'Your Message' },
];

function FranchiseEnquiry() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    location: '',
    investment: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formStatus, setFormStatus] = useState(null);
  const [errors, setErrors] = useState({});

  // Validation function
  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = 'Name is required';
    if (!formData.email.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)) newErrors.email = 'Invalid email format';
    if (!formData.phone.match(/^\+?[\d\s-]{10,}$/)) newErrors.phone = 'Invalid phone number';
    if (!formData.location.trim()) newErrors.location = 'Location is required';
    if (!formData.investment.trim()) newErrors.investment = 'Investment capacity is required';
    if (!formData.message.trim()) newErrors.message = 'Message is required';
    return newErrors;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    setErrors((prev) => ({ ...prev, [name]: '' }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/franchise-enquiry`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.message || 'Failed to submit enquiry');
      }

      setFormStatus('success');
      setFormData({ name: '', email: '', phone: '', location: '', investment: '', message: '' });
      setErrors({});
      setTimeout(() => setFormStatus(null), 5000);
    } catch (error) {
      setFormStatus('error');
      setErrors({ general: error.message || 'An error occurred. Please try again.' });
      setTimeout(() => setFormStatus(null), 5000);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Animation variants
  const sectionVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: 'easeOut' } },
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 15 },
    visible: (i) => ({
      opacity: 1,
      y: 0,
      transition: { delay: i * 0.2, duration: 0.5, ease: 'easeOut' },
    }),
  };

  const inputVariants = {
    initial: { scale: 1 },
    focus: {
      scale: 1.02,
      transition: { duration: 0.3 },
    },
  };

  const buttonVariants = {
    initial: { scale: 1 },
    hover: { scale: 1.05 },
    tap: { scale: 0.95 },
  };

  return (
    <main className="enquir-main">
      <motion.section
        className="enquir-hero-section"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.8 }}
      >
        <div className="enquir-hero-content">
          <motion.h1
            className="enquir-hero-title"
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.2, duration: 0.6 }}
          >
            Join Our Franchise
          </motion.h1>
          <motion.p
            className="enquir-hero-subtitle"
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.4, duration: 0.6 }}
          >
            Become part of Beaver Tours and bring our cycling adventures to your community!
          </motion.p>
        </div>
      </motion.section>

      <motion.section
        className="enquir-info-section"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.3 }}
        variants={sectionVariants}
      >
        <div className="enquir-section-container">
          <h2 className="enquir-section-title">Why Franchise with Us?</h2>
          <div className="enquir-info-grid">
            {[
              {
                icon: '🚴',
                title: 'Proven Model',
                content: 'Benefit from our established brand and successful business model.',
              },
              {
                icon: '🤝',
                title: 'Support',
                content: 'Receive comprehensive training and ongoing support.',
              },
              {
                icon: '📈',
                title: 'Growth',
                content: 'Tap into the growing demand for eco-friendly travel experiences.',
              },
            ].map((item, index) => (
              <motion.div
                key={index}
                className="enquir-info-card"
                custom={index}
                variants={cardVariants}
                whileHover={{ scale: 1.05 }}
                role="article"
                aria-labelledby={`info-card-title-${index}`}
              >
                <motion.div
                  className="enquir-info-icon"
                  whileHover={{ rotate: 10 }}
                  transition={{ duration: 0.3 }}
                >
                  {item.icon}
                </motion.div>
                <h3 id={`info-card-title-${index}`}>{item.title}</h3>
                <p>{item.content}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </motion.section>

      <motion.section
        className="enquir-form-section"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.3 }}
        variants={sectionVariants}
      >
        <div className="enquir-section-container">
          <h2 className="enquir-section-title">Franchise Enquiry Form</h2>
          <motion.form
            className="enquir-form"
            onSubmit={handleSubmit}
            noValidate
            aria-live="polite"
          >
            <div className="enquir-form-row">
              {formFields.slice(0, 2).map((field, index) => (
                <motion.div
                  key={field.name}
                  className="enquir-form-group"
                  variants={cardVariants}
                  custom={index}
                >
                  <label htmlFor={field.name}>{field.label}</label>
                  <motion.input
                    type={field.type}
                    id={field.name}
                    name={field.name}
                    placeholder={field.placeholder}
                    value={formData[field.name]}
                    onChange={handleChange}
                    required
                    variants={inputVariants}
                    initial="initial"
                    whileFocus="focus"
                    aria-describedby={errors[field.name] ? `${field.name}-error` : undefined}
                  />
                  {errors[field.name] && (
                    <p className="enquir-form-error" id={`${field.name}-error`}>
                      {errors[field.name]}
                    </p>
                  )}
                </motion.div>
              ))}
            </div>
            <div className="enquir-form-row">
              {formFields.slice(2, 4).map((field, index) => (
                <motion.div
                  key={field.name}
                  className="enquir-form-group"
                  variants={cardVariants}
                  custom={index + 2}
                >
                  <label htmlFor={field.name}>{field.label}</label>
                  <motion.input
                    type={field.type}
                    id={field.name}
                    name={field.name}
                    placeholder={field.placeholder}
                    value={formData[field.name]}
                    onChange={handleChange}
                    required
                    variants={inputVariants}
                    initial="initial"
                    whileFocus="focus"
                    aria-describedby={errors[field.name] ? `${field.name}-error` : undefined}
                  />
                  {errors[field.name] && (
                    <p className="enquir-form-error" id={`${field.name}-error`}>
                      {errors[field.name]}
                    </p>
                  )}
                </motion.div>
              ))}
            </div>
            <div className="enquir-form-row">
              {formFields.slice(4, 5).map((field, index) => (
                <motion.div
                  key={field.name}
                  className="enquir-form-group"
                  variants={cardVariants}
                  custom={index + 4}
                >
                  <label htmlFor={field.name}>{field.label}</label>
                  <motion.input
                    type={field.type}
                    id={field.name}
                    name={field.name}
                    placeholder={field.placeholder}
                    value={formData[field.name]}
                    onChange={handleChange}
                    required
                    variants={inputVariants}
                    initial="initial"
                    whileFocus="focus"
                    aria-describedby={errors[field.name] ? `${field.name}-error` : undefined}
                  />
                  {errors[field.name] && (
                    <p className="enquir-form-error" id={`${field.name}-error`}>
                      {errors[field.name]}
                    </p>
                  )}
                </motion.div>
              ))}
            </div>
            {formFields.slice(5).map((field, index) => (
              <motion.div
                key={field.name}
                className="enquir-form-group enquir-form-group-full"
                variants={cardVariants}
                custom={index + 5}
              >
                <label htmlFor={field.name}>{field.label}</label>
                <motion.textarea
                  id={field.name}
                  name={field.name}
                  placeholder={field.placeholder}
                  value={formData[field.name]}
                  onChange={handleChange}
                  required
                  variants={inputVariants}
                  initial="initial"
                  whileFocus="focus"
                  aria-describedby={errors[field.name] ? `${field.name}-error` : undefined}
                />
                {errors[field.name] && (
                  <p className="enquir-form-error" id={`${field.name}-error`}>
                    {errors[field.name]}
                  </p>
                )}
              </motion.div>
            ))}
            {errors.general && (
              <p className="enquir-form-error enquir-form-error-general">{errors.general}</p>
            )}
            <motion.button
              type="submit"
              className="enquir-form-submit-button"
              variants={buttonVariants}
              initial="initial"
              whileHover="hover"
              whileTap="tap"
              disabled={isSubmitting}
              aria-disabled={isSubmitting}
            >
              {isSubmitting ? (
                <motion.span
                  animate={{ rotate: 360 }}
                  transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                >
                  ⏳
                </motion.span>
              ) : (
                'Submit'
              )}
            </motion.button>
            <AnimatePresence>
              {formStatus && (
                <motion.p
                  className={`enquir-form-status ${formStatus}`}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.3 }}
                  role="alert"
                >
                  {formStatus === 'success'
                    ? 'Enquiry sent!'
                    : 'Error. Try again.'}
                </motion.p>
              )}
            </AnimatePresence>
          </motion.form>
        </div>
      </motion.section>
    </main>
  );
}

export default FranchiseEnquiry;